import React, { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import {
  Download,
  MoreVertical,
  Search,
  Minus,
  Plus,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  CheckCircle2,
  XCircle,
  Clock,
  Plus as PlusIcon,
  Upload,
  Printer,
  ShieldCheck,
  FileText
} from 'lucide-react';
import styles from './DesignApproval.module.css';

const DesignApproval = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [markedPdf, setMarkedPdf] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMarkedPdf(file);
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) {
      setMarkedPdf(file);
    }
  };

  const summaryData = {
    woNumber: 'WO-9842',
    linkedPo: 'PO-1029',
    vendor: 'Advanced Circuits',
    drawingType: 'Electrical',
    version: 'v2',
    dueDate: 'Oct 30, 2023',
    dateSubmitted: 'Oct 24, 2023',
    priority: 'High',
    submittedBy: {
      name: 'Sarah Jenkins',
      initials: 'SJ'
    }
  };

  const revisionHistory = [
    {
      version: 'v2 Submitted',
      date: 'Today, 10:45 AM',
      comment: 'Sarah Jenkins uploaded drawing_v2.pdf with updated PCB layout.',
      status: 'submitted',
      active: true
    },
    {
      version: 'v1 Rejected',
      date: 'Oct 23, 03:12 PM',
      comment: 'Michael Chen requested revisions: "Missing ground plane clearance on the north sector."',
      status: 'rejected'
    },
    {
      version: 'v1 Submitted',
      date: 'Oct 22, 09:00 AM',
      comment: 'Initial submission by Sarah Jenkins.',
      status: 'uploaded'
    }
  ];

  return (
    <div className={styles['design-approval-container']}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
{isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={[styles['main-content'], styles['approval-main']].join(' ')}>
          {/* Header Row */}
          <div className={styles['approval-header-row']}>
            <div className={styles['header-left-nav']}>
              <Breadcrumbs customItems={[
                { label: 'Projects', path: '/projects' },
                { label: 'Design Submissions', path: '/projects/design-submissions' },
                { label: `Approval DS-${id}` }
              ]} />
              <div className={styles['title-with-badge']}>
                <h1>Design Approval</h1>
                <span className={styles['status-badge-pending']}>PENDING REVIEW</span>
              </div>
            </div>
            <div className={styles['header-right-actions']}>
              <button className={styles['btn-download-pdf']}>
                <FileText size={18} /> Download PDF
              </button>
              <button className={styles['btn-more']}>
                <MoreVertical size={20} />
              </button>
            </div>
          </div>

          <div className={styles['approval-layout-grid']}>
            {/* Left Column: Summary & Controls */}
            <aside className={styles['approval-control-panel']}>
              {/* Summary Snapshot */}
              <div className={styles['approval-card']}>
                <h3 className={styles['card-lbl']}>SUMMARY SNAPSHOT</h3>
                <div className={styles['snapshot-grid']}>
                  <div className={styles['snap-item']}>
                    <label>WO #</label>
                    <p>{summaryData.woNumber}</p>
                  </div>
                  <div className={styles['snap-item']}>
                    <label>LINKED PO</label>
                    <p className={styles['text-orange']}>{summaryData.linkedPo}</p>
                  </div>
                  <div className={styles['snap-item']}>
                    <label>VENDOR</label>
                    <p>{summaryData.vendor}</p>
                  </div>
                  <div className={styles['snap-item']}>
                    <label>DRAWING TYPE</label>
                    <p>{summaryData.drawingType}</p>
                  </div>
                  <div className={styles['snap-item']}>
                    <label>VERSION</label>
                    <p>{summaryData.version}</p>
                  </div>
                  <div className={styles['snap-item']}>
                    <label>DUE DATE</label>
                    <p className={styles['text-red']}>{summaryData.dueDate}</p>
                  </div>
                  <div className={styles['snap-item']}>
                    <label>DATE SUBMITTED</label>
                    <p>{summaryData.dateSubmitted}</p>
                  </div>
                  <div className={styles['snap-item']}>
                    <label>PRIORITY</label>
                    <p className={styles['text-red']}>! {summaryData.priority}</p>
                  </div>
                </div>
                <div className={styles['submitted-by-row']}>
                  <label>SUBMITTED BY</label>
                  <div className={styles['user-pill-small']}>
                    <span className={styles['user-initials']}>{summaryData.submittedBy.initials}</span>
                    <span className={styles['user-name']}>{summaryData.submittedBy.name}</span>
                  </div>
                </div>
              </div>

              {/* Review Decision */}
              <div className={styles['approval-card']}>
                <h3 className={styles['card-lbl']}>Review Decision</h3>
                <div className={styles['decision-form']}>
                  <div className={styles['form-group-approval']}>
                    <label>Review Comments <span className={styles['text-red']}>*</span></label>
                    <textarea 
                      placeholder="Enter detailed feedback for the engineering team..." 
                      className={styles['approval-textarea']}
                    ></textarea>
                  </div>

                  <div className={styles['form-group-approval']}>
                    <label>Attach Marked PDF</label>
                    <div 
                      className={`${styles['mini-dropzone']} ${markedPdf ? styles['has-file'] : ''}`}
                      onClick={() => fileInputRef.current.click()}
                      onDragOver={onDragOver}
                      onDrop={onDrop}
                    >
                      <input 
                        type="file" 
                        ref={fileInputRef} 
                        style={{ display: 'none' }} 
                        onChange={handleFileChange}
                        accept=".pdf"
                      />
                      {markedPdf ? (
                        <div className={styles['mini-file-info']}>
                          <FileText size={20} className={styles['text-green']} />
                          <div className={styles['mini-file-texts']}>
                            <span className={styles['file-name']}>{markedPdf.name}</span>
                            <span className={styles['file-meta']}>{(markedPdf.size / 1024).toFixed(1)} KB • Replace</span>
                          </div>
                        </div>
                      ) : (
                        <>
                          <Upload size={20} className={styles['text-muted']} />
                          <p>Drop files or <span>browse</span></p>
                        </>
                      )}
                    </div>
                  </div>

                  <div className={styles['decision-actions']}>
                    <button className={styles['btn-approve-large']}>
                      <CheckCircle2 size={20} /> Approve Design
                    </button>
                    <button className={styles['btn-reject-large']}>
                      <XCircle size={20} /> Reject & Request Revision
                    </button>
                  </div>
                </div>
              </div>

              {/* Revision History */}
              <div className={[styles['approval-card'], styles['no-padding']].join(' ')}>
                <div className={styles['card-header-padded']}>
                  <h3 className={styles['card-lbl']}>REVISION HISTORY</h3>
                </div>
                <div className={styles['vertical-history-timeline']}>
                  {revisionHistory.map((item, idx) => (
                    <div key={idx} className={`${styles['history-item']} ${item.active ? styles['active'] : ''}`}>
                      <div className={styles['history-marker']}>
                        <div className={`${styles['dot']} ${item.status}`}>
                          {item.status === 'submitted' && <div className={styles['inner-dot']} />}
                          {item.status === 'rejected' && <div className={styles['rejected-line']} />}
                          {item.status === 'uploaded' && <div className={styles['plus-symbol']} />}
                        </div>
                        {idx !== revisionHistory.length - 1 && <div className={styles['line']} />}
                      </div>
                      <div className={styles['history-content']}>
                        <div className={styles['history-title-row']}>
                          <h4>{item.version}</h4>
                          <span className={styles['history-date']}>{item.date}</span>
                        </div>
                        <p className={styles['history-comment']}>{item.comment}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </aside>

            {/* Right Column: Viewer Area */}
            <section className={[styles['approval-viewer-panel'], styles['card']].join(' ')}>
              <div className={styles['viewer-toolbar-approval']}>
                <div className={[styles['toolbar-segment'], styles['search-zoom']].join(' ')}>
                  <button className={styles['tool-btn']}><Search size={18} /></button>
                  <div className={styles['zoom-controls']}>
                    <button onClick={() => setZoom(zoom - 10)}><Minus size={16} /></button>
                    <span className={styles['zoom-val']}>{zoom}%</span>
                    <button onClick={() => setZoom(zoom + 10)}><Plus size={16} /></button>
                  </div>
                </div>

                <div className={[styles['toolbar-segment'], styles['page-nav']].join(' ')}>
                  <button className={styles['tool-btn']}><ChevronLeft size={18} /></button>
                  <span className={styles['page-indicator']}>Page <strong>1</strong> of 4</span>
                  <button className={styles['tool-btn']}><ChevronRight size={18} /></button>
                </div>

                <div className={[styles['toolbar-segment'], styles['extra-tools']].join(' ')}>
                   <button className={styles['tool-btn-text']}><Maximize2 size={16} /> Fullscreen</button>
                   <button className={styles['btn-download-viewer']}><Download size={16} /> Download</button>
                </div>
              </div>

              <div className={styles['drawing-display-container']}>
                <div className={styles['drawing-canvas']}>
                  <div className={styles['drawing-watermark']}>CONFIDENTIAL</div>
                  <div className={styles['drawing-meta-overlay']}>
                    <span>DRW NO: AD-2023-01</span>
                    <span>SHEET: 1 OF 4</span>
                  </div>
                  {/* Mock Drawing elements */}
                  <div className={styles['drawing-schematic-mock']}>
                    <div className={styles['circle-main']} />
                    <div className={styles['rect-grid']}>
                      <div className={styles['line-h']} />
                      <div className={styles['line-h']} />
                      <div className={styles['line-h']} />
                    </div>
                    <div className={styles['footer-blocks']}>
                        <div className={styles['block']} />
                        <div className={styles['block']} />
                        <div className={styles['block']} />
                    </div>
                    <div className={styles['advanced-circuits-logo']}>
                        ADVANCED CIRCUITS ENGINEERING
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles['viewer-footer-info']}>
                <div className={styles['file-info-left']}>
                  <div className={styles['pdf-icon-box']}><FileText size={20} /></div>
                  <div className={styles['file-texts']}>
                    <h4>drawing_v2_electrical_main.pdf</h4>
                    <p>4.2 MB • Updated 4 hours ago</p>
                  </div>
                </div>
                <div className={styles['file-info-right']}>
                  <span className={styles['info-tag']}><Printer size={16} /> Ready for Print</span>
                  <span className={styles['info-tag']}><ShieldCheck size={16} /> Signature Valid</span>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DesignApproval;
